/* ==========================================================================
   MKTheme — product JavaScript
   Gallery, variant picker, complementary products, sticky buy bar, share.
   Shared by the main-product and featured-product sections. (Section
   {% javascript %} is only bundled on pages that render that section, so
   this lives in an asset both sections load.)
   ========================================================================== */
(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || (window.MKTheme && window.MKTheme.settings && window.MKTheme.settings.moneyFormat) || ('$' + '{' + '{amount}}');
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const withDelimiters = (number, precision, thousands, decimal) => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  const publish = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  /* ---------- Media gallery ---------- */
  if (!customElements.get('media-gallery')) {
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = [...this.querySelectorAll('.gallery__slide')];
        if (!this.track || this.slides.length === 0) return;
        this.lightbox = this.querySelector('[data-lightbox]');

        this.addEventListener('click', (e) => {
          const goto = e.target.closest('[data-goto]');
          if (goto) return this.goTo(parseInt(goto.dataset.goto, 10));
          if (e.target.closest('[data-gallery-prev]')) return this.goTo(this.index - 1);
          if (e.target.closest('[data-gallery-next]')) return this.goTo(this.index + 1);
          const zoom = e.target.closest('[data-zoom]');
          if (zoom) return this.openLightbox(parseInt(zoom.dataset.zoom, 10));
          if (e.target.closest('[data-lightbox-close]')) return this.lightbox?.close();
          if (e.target === this.lightbox) this.lightbox.close();
        });

        let raf;
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            if (!this.isScroller() || this.animating) return;
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            if (i !== this.index) this.setActive(i);
          });
        }, { passive: true });

        const active = this.slides.findIndex((s) => s.classList.contains('is-active'));
        this.index = 0;
        if (active > 0) requestAnimationFrame(() => this.goTo(active, false));
        else this.setActive(0);
      }

      isScroller() {
        return this.track.scrollWidth > this.track.clientWidth + 2;
      }

      goTo(i, smooth = true) {
        const count = this.slides.length;
        const index = ((i % count) + count) % count;
        const slide = this.slides[index];
        if (!slide) return;
        if (this.isScroller()) {
          this.scrollTrack(slide.offsetLeft - this.track.offsetLeft, smooth);
        } else if (smooth && index !== this.index) {
          const rect = slide.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) slide.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.setActive(index);
      }

      // Animates the track ourselves: native smooth scrolling is cancelled by
      // other smooth scrolls and fights scroll-snap, which left arrows dead.
      scrollTrack(left, smooth) {
        const track = this.track;
        cancelAnimationFrame(this.scrollAnim);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!smooth || reduce) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = left;
          track.style.scrollBehavior = '';
          return;
        }
        const from = track.scrollLeft;
        const distance = left - from;
        const duration = 380;
        const start = performance.now();
        this.animating = true;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        const step = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          track.scrollLeft = from + distance * eased;
          if (t < 1) {
            this.scrollAnim = requestAnimationFrame(step);
          } else {
            track.style.scrollSnapType = '';
            track.style.scrollBehavior = '';
            this.animating = false;
          }
        };
        this.scrollAnim = requestAnimationFrame(step);
      }

      goToMedia(mediaId) {
        if (!mediaId) return;
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (index >= 0) this.goTo(index);
      }

      setActive(index) {
        this.index = index;
        this.slides.forEach((s, i) => {
          const active = i === index;
          s.classList.toggle('is-active', active);
          if (!active) {
            s.querySelectorAll('video').forEach((v) => v.pause());
            s.querySelectorAll('iframe').forEach((f) => {
              try {
                f.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
                f.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
              } catch (e) {}
            });
          }
        });
        this.querySelectorAll('[data-goto]').forEach((el) => el.classList.toggle('is-active', parseInt(el.dataset.goto, 10) === index));
        const counter = this.querySelector('[data-gallery-current]');
        if (counter) counter.textContent = index + 1;
        // Keep the active thumbnail in view by scrolling its strip directly.
        // (scrollIntoView with smooth behavior cancels the main track's smooth
        // scroll in Chrome, and can also scroll the whole page.)
        const thumb = this.querySelector(`.gallery__thumb[data-goto="${index}"]`);
        const strip = thumb && thumb.parentElement.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const item = thumb.parentElement.getBoundingClientRect();
          const box = strip.getBoundingClientRect();
          strip.scrollLeft += item.left - box.left - (box.width - item.width) / 2;
        }
      }

      openLightbox(index) {
        if (!this.lightbox) return;
        this.lightbox.showModal();
        const item = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
        if (item) item.scrollIntoView({ block: 'start' });
      }
    }
    customElements.define('media-gallery', MediaGallery);
  }

  /* ---------- Variant picker ---------- */
  if (!customElements.get('variant-picker')) {
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-variants]');
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.addEventListener('change', () => this.onChange());
        this.updateAvailability();
      }

      selectedOptions() {
        const options = [];
        this.querySelectorAll('select[data-option-index]').forEach((s) => { options[parseInt(s.dataset.optionIndex, 10)] = s.value; });
        this.querySelectorAll('input[type="radio"][data-option-index]:checked').forEach((r) => { options[parseInt(r.dataset.optionIndex, 10)] = r.value; });
        return options;
      }

      onChange() {
        const options = this.selectedOptions();
        const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i])) || null;
        this.querySelectorAll('fieldset').forEach((fs) => {
          const checked = fs.querySelector('input:checked');
          const label = fs.querySelector('[data-selected-value]');
          if (checked && label) label.textContent = checked.value;
        });
        this.updateAvailability();
        const info = this.closest('product-info');
        if (info && info.updateVariant) info.updateVariant(variant);
      }

      updateAvailability() {
        const selected = this.selectedOptions();
        const check = (index, value) => this.variants.some((v) => v.available && v.options[index] === value && v.options.every((o, i) => i === index || i > index || o === selected[i]));
        this.querySelectorAll('input[type="radio"][data-option-index]').forEach((input) => {
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !check(parseInt(input.dataset.optionIndex, 10), input.value));
        });
        this.querySelectorAll('select[data-option-index]').forEach((select) => {
          const index = parseInt(select.dataset.optionIndex, 10);
          [...select.options].forEach((opt) => opt.classList.toggle('is-unavailable', !check(index, opt.value)));
        });
      }
    }
    customElements.define('variant-picker', VariantPicker);
  }

  /* ---------- Product info: variant updates ---------- */
  if (!customElements.get('product-info')) {
    class ProductInfo extends HTMLElement {
      get section() {
        return this.closest('.shopify-section') || document;
      }

      updateVariant(variant) {
        const strings = (window.MKTheme && window.MKTheme.strings) || {};
        const section = this.section;
        const sectionId = this.dataset.sectionId;

        section.querySelectorAll('[data-variant-id-input]').forEach((input) => {
          input.value = variant ? variant.id : '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const preOrder = this.dataset.preOrder === 'true';
        section.querySelectorAll('[data-add-button]').forEach((button) => {
          const label = button.querySelector('[data-add-label]');
          const available = variant && variant.available;
          button.disabled = !available;
          if (label) label.textContent = !variant ? strings.unavailable : !available ? strings.soldOut : preOrder ? strings.preOrder : strings.addToCart;
        });
        section.querySelectorAll('.product-form__dynamic').forEach((el) => { el.hidden = !(variant && variant.available); });

        if (!variant) return;

        // Immediate client-side price update; refined by the section render below.
        section.querySelectorAll('[data-region="price"]').forEach((region) => {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          region.innerHTML = `<div class="price${onSale ? ' price--on-sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>${onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}</div>`;
        });

        const gallery = section.querySelector('media-gallery');
        if (gallery && variant.featured_media) gallery.goToMedia(variant.featured_media.id);

        const detail = { variant, productId: this.dataset.productId, sectionId };
        publish('variant:changed', detail);
        publish('variant:change', detail); // legacy

        if (this.hasAttribute('data-featured')) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        this.abort?.abort();
        this.abort = new AbortController();
        fetch(`${this.dataset.productUrl}?variant=${variant.id}&section_id=${sectionId}`, { signal: this.abort.signal })
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            ['price', 'badges', 'sku', 'inventory', 'sticky-button'].forEach((name) => {
              const current = section.querySelectorAll(`[data-region="${name}"]`);
              const fresh = doc.querySelectorAll(`[data-region="${name}"]`);
              current.forEach((el, i) => { if (fresh[i]) el.innerHTML = fresh[i].innerHTML; });
            });
          })
          .catch(() => {});
      }
    }
    customElements.define('product-info', ProductInfo);
  }

  /* ---------- Complementary products ---------- */
  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url || this.children.length) return;
        const io = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const fresh = doc.querySelector(`product-recommendations[data-block-id="${this.dataset.blockId}"]`);
              if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
              else this.closest('.complementary')?.setAttribute('hidden', '');
            })
            .catch(() => {});
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      }
    }
    customElements.define('product-recommendations', ProductRecommendations);
  }

  /* ---------- Sticky buy bar ---------- */
  if (!customElements.get('sticky-buy-bar')) {
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        const section = this.closest('.shopify-section') || document;
        const target = section.querySelector('[data-buy-buttons]');
        if (!target) return;
        this.hidden = false;
        const footer = document.querySelector('.section-footer') || document.querySelector('footer');
        let pastButton = false;
        let footerVisible = false;
        const update = () => this.classList.toggle('is-visible', pastButton && !footerVisible);
        new IntersectionObserver(([entry]) => {
          pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          update();
        }).observe(target);
        if (footer) {
          new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }).observe(footer);
        }
      }
    }
    customElements.define('sticky-buy-bar', StickyBuyBar);
  }

  /* ---------- Share ---------- */
  if (!customElements.get('share-button')) {
    class ShareButton extends HTMLElement {
      connectedCallback() {
        this.querySelector('[data-share]')?.addEventListener('click', async () => {
          const data = { url: this.dataset.url, title: this.dataset.title };
          if (navigator.share) {
            try { await navigator.share(data); } catch (e) {}
            return;
          }
          try {
            await navigator.clipboard.writeText(data.url);
            const ok = this.querySelector('[data-share-success]');
            if (ok) { ok.hidden = false; setTimeout(() => { ok.hidden = true; }, 2500); }
          } catch (e) {}
        });
      }
    }
    customElements.define('share-button', ShareButton);
  }
})();
